import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Heart, 
  Users, 
  Calendar, 
  Leaf, 
  HandHeart, 
  ArrowRight,
  Sparkles,
  Globe,
  Shield
} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Supportive Community',
      description: 'A space free from judgment, competition, or comparison. We celebrate every commitment, big or small.'
    },
    {
      icon: Leaf,
      title: 'Simple & Intentional',
      description: 'One day at a time. No streaks, no leaderboards—just personal commitment and collective encouragement.'
    },
    {
      icon: HandHeart,
      title: 'Purpose-Driven',
      description: 'Fast for health, clarity, spirituality, solidarity, or any reason meaningful to you. Optionally support a cause.'
    },
    {
      icon: Shield,
      title: 'Privacy-First',
      description: 'Choose how you appear—full name, first name only, or completely anonymous. Your journey, your choice.'
    }
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Pick a Day',
      description: 'Browse the calendar and choose a date for your one-day fast.'
    },
    {
      step: '2',
      title: 'Make Your Commitment',
      description: 'Share your intention, location, and optionally a cause you\'re supporting.'
    },
    {
      step: '3',
      title: 'Join the Community',
      description: 'See who else is fasting, share encouragement, and inspire others.'
    },
    {
      step: '4',
      title: 'Grow Together',
      description: 'Track your journey, earn achievement badges, and build healthy habits.'
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="about-page">
      <div className="grain-overlay" />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Globe className="h-4 w-4" />
              A Global Community
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Fasting Together, 
              <span className="text-primary"> One Day at a Time</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              One Day Fast is a free community platform where people commit to single-day fasts, 
              share their intentions, and support one another—all in a calm, non-competitive space.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/">
                <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg btn-lift">
                  View the Calendar
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg">
                  Join for Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What We Are / What We're Not */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          <Card className="bg-sage-50/50 border-sage-200 animate-fade-in-up">
            <CardContent className="p-6 sm:p-8">
              <h3 className="font-display text-xl text-sage-700 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                What We Are
              </h3>
              <ul className="space-y-3 text-foreground/80">
                <li className="flex items-start gap-2">
                  <span className="text-sage-500 mt-1">✓</span>
                  A calendar-driven community platform
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sage-500 mt-1">✓</span>
                  An encouragement and inspiration space
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sage-500 mt-1">✓</span>
                  A simple, reflective fasting commitment tool
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sage-500 mt-1">✓</span>
                  Completely free to use
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-stone-50/50 border-stone-200 animate-fade-in-up stagger-1">
            <CardContent className="p-6 sm:p-8">
              <h3 className="font-display text-xl text-stone-600 mb-4">What We're Not</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-stone-400 mt-1">✗</span>
                  A diet or weight-loss app
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-stone-400 mt-1">✗</span>
                  A medical or health advice platform
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-stone-400 mt-1">✗</span>
                  A donation-processing site
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-stone-400 mt-1">✗</span>
                  A competitive or gamified tracker
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Our Values */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-4">Our Values</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Everything we build is guided by these principles
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <Card 
              key={value.title}
              className="bg-white/60 backdrop-blur-sm border-border/50 animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display text-lg text-foreground mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-secondary/30 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-4">How It Works</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Getting started is simple
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {howItWorks.map((item, index) => (
              <div 
                key={item.step}
                className="flex gap-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white font-display text-lg flex items-center justify-center">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-display text-lg text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Charity Note */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="bg-white/60 backdrop-blur-sm border-border/50 animate-fade-in-up">
          <CardContent className="p-8 text-center">
            <HandHeart className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="font-display text-2xl text-foreground mb-4">Supporting Causes</h3>
            <p className="text-muted-foreground mb-4">
              When you commit to a fast, you can optionally associate it with a charity or cause 
              close to your heart. This is purely symbolic—we don't process any donations.
            </p>
            <p className="text-sm text-muted-foreground italic">
              "Consider donating what you might have spent on food today, independently, 
              to your chosen cause."
            </p>
          </CardContent>
        </Card>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-primary/5 rounded-2xl p-8 sm:p-12 text-center animate-fade-in-up">
          <Users className="h-12 w-12 text-primary mx-auto mb-4" />
          <h2 className="font-display text-2xl sm:text-3xl text-foreground mb-4">
            Ready to Join?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            Pick a day, make your commitment, and become part of a quiet community 
            turning personal intention into collective encouragement.
          </p>
          <Link to="/register">
            <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg btn-lift">
              Get Started — It's Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
